import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  format,
} from "date-fns";
import { Utility } from "./utility";

export const formatCompactAgo = (from: Date): string => {
  const now = new Date();
  const diffInMinutes = differenceInMinutes(now, from);

  if (diffInMinutes < 1) {
    return "now";
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = differenceInHours(now, from);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = differenceInDays(now, from);
  const remainingHours = diffInHours % 24;

  if (remainingHours === 0) {
    return `${diffInDays}d ago`;
  } else {
    return `${diffInDays}d ${remainingHours}h ago`;
  }
};

export abstract class SyncStatus<T> extends Utility<T> {
  status!: string;
  api_called_at!: string;
  updated_at!: string;
  last_succeeded_at!: string | null;

  get local_updated_at(): Date {
    return this.get_local_time(this.updated_at);
  }

  get local_updated_at_datetime_string(): string {
    return format(this.local_updated_at, "MMM d, hh:mm aaa");
  }

  get last_updated_compact(): string {
    return formatCompactAgo(this.local_updated_at);
  }

  get local_last_succeeded_at(): Date | null {
    return this.last_succeeded_at
      ? this.get_local_time(this.last_succeeded_at)
      : null;
  }

  get last_succeeded_compact(): string | null {
    const succeededAt = this.local_last_succeeded_at;
    return succeededAt ? formatCompactAgo(succeededAt) : null;
  }

  get is_waiting(): boolean {
    return this.status === "waiting";
  }

  get is_succeeded(): boolean {
    return this.status === "succeeded";
  }

  get is_failed(): boolean {
    return this.status === "failed";
  }

  get is_update_recommended(): boolean {
    if (this.is_failed) return true;
    if (this.is_waiting) return false;
    return differenceInDays(new Date(), this.local_updated_at) >= 1;
  }

  get estimated_sync_time(): number {
    const updatedAt = new Date(this.updated_at);
    const apiCalledAt = new Date(this.api_called_at);

    const timeDiffInMs = updatedAt.getTime() - apiCalledAt.getTime();
    return Math.floor(timeDiffInMs / (1000 * 60));
  }

  get estimated_sync_time_string(): string {
    const hours = Math.floor(this.estimated_sync_time / 60);
    let minutes = this.estimated_sync_time % 60;

    if (minutes < 1) {
      minutes = 1;
    }

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
}
